import "./index.scss";
import { ContentfulLivePreview } from "@contentful/live-preview";
import {
  GeneralMediumClose,
  GeneralMediumSearch,
} from "@wise-emotions/react-icons";
import { HeaderFullDataProps } from "components/HeaderNew/HeaderFull/types";
import { LiveInspectorData } from "types/types";
import { Poly } from "components/Poly";
import HamburgerMenu from "components/HeaderNew/components/HamburgerMenu";
import HeaderTop from "components/HeaderNew/components/HeaderTop";
import LangSelect from "components/HeaderNew/components/LangSelect";
import MenuOverlaySlider from "components/HeaderNew/components/MenuOverlaySlider";
import PrivateAreaButtons from "components/HeaderNew/HeaderFull/components/PrivateAreaButtons";
import React, { useEffect, useRef, useState } from "react";
import SearchOverlay from "components/Header/searchOverlay";
import SubmenuButtons from "components/HeaderNew/HeaderFull/components/SubmenuButtons";

export type HeaderFullProps = {
  data: HeaderFullDataProps;
  searchContent?: React.ReactNode;
  location: { href: string; pathname: string };
};

export type MenuOpeningStatus =
  | "fullClose"
  | "close"
  | "submenuOpen"
  | "groupOpen";

const MIN_SCROLL_VALUE_TO_HIDE_TOP_HEADER = 150;

const HeaderFull = ({ data, searchContent, location }: HeaderFullProps) => {
  const headerTopRef = useRef<HTMLDivElement>(null);
  const headerBottomRef = useRef<HTMLDivElement>(null);
  const bodyTag = useRef<HTMLBodyElement | null>(null);
  const [menuOpeningStatus, setMenuOpeningStatus] =
    useState<MenuOpeningStatus>("close");
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const wasHeaderTopClosed = useRef(false);

  const hideHeaderTopAndTraslateHeaderBottom = (
    shouldHide = wasHeaderTopClosed.current,
  ) => {
    headerTopRef?.current?.classList.toggle("header-top--hide", shouldHide);
    headerBottomRef?.current?.classList.toggle(
      "header-full__bottom--traslated",
      shouldHide,
    );
  };

  const manageMenuOpeningForHamburgerMenu = (status: MenuOpeningStatus) => {
    setMenuOpeningStatus(status);
    if (status === "groupOpen" || status === "submenuOpen")
      hideHeaderTopAndTraslateHeaderBottom(false);
    else hideHeaderTopAndTraslateHeaderBottom();
    if (isSearchOpen) setIsSearchOpen(false);
  };

  const manageSearchOpening = () => {
    setIsSearchOpen((value) => {
      if (!value) hideHeaderTopAndTraslateHeaderBottom(false);
      else hideHeaderTopAndTraslateHeaderBottom();
      return !value;
    });

    if (menuOpeningStatus) setMenuOpeningStatus("fullClose");
  };

  const currentMenu =
    data.menu.find(({ anchorProps: { href, to } }) =>
      location.pathname.includes(href || to || ""),
    ) || data.menu[0];

  const currentSubmenu = currentMenu?.submenu.find(
    ({ anchorProps: { href, to } }) =>
      location.pathname.includes(href || to || ""),
  );

  useEffect(() => {
    let prevPosition = 0;
    let timeout: NodeJS.Timeout;

    const callbackScroll = () => {
      const scrollFromTop = document.documentElement.scrollTop;
      const shouldHideHeader =
        scrollFromTop >= MIN_SCROLL_VALUE_TO_HIDE_TOP_HEADER &&
        prevPosition <= scrollFromTop;

      if (timeout) {
        clearTimeout(timeout);
      }

      timeout = setTimeout(
        () => {
          headerTopRef?.current?.classList.toggle(
            "header-top--hide",
            shouldHideHeader,
          );
          headerBottomRef?.current?.classList.toggle(
            "header-full__bottom--traslated",
            shouldHideHeader,
          );
        },
        document.documentElement.scrollTop === 0 ? 0 : 200,
      );

      prevPosition = document.documentElement.scrollTop;
      wasHeaderTopClosed.current = shouldHideHeader;
    };

    window.addEventListener("scroll", callbackScroll);

    return () => {
      window.removeEventListener("scroll", callbackScroll);
      clearTimeout(timeout);
    };
  }, [menuOpeningStatus]);

  useEffect(() => {
    bodyTag.current = document.querySelector("body");

    if (bodyTag.current) {
      if (isSearchOpen) {
        bodyTag.current.classList.add("no-scroll-search-opened");
      } else {
        bodyTag.current.classList.remove("no-scroll-search-opened");
      }
      if (menuOpeningStatus !== "close" && menuOpeningStatus !== "fullClose") {
        bodyTag.current.classList.add("no-scroll-menu-opened");
      } else {
        bodyTag.current.classList.remove("no-scroll-menu-opened");
      }
    }
  }, [menuOpeningStatus, isSearchOpen]);

  const liveInspector: LiveInspectorData = {
    header: ContentfulLivePreview.getProps({
      entryId: data.contentful_id,
      fieldId: "entryTitle",
    }),
  };

  return (
    <header className="header-full" {...liveInspector.header}>
      <HeaderTop
        ref={headerTopRef}
        location={location}
        hasLanguageSelect
        {...data}
      />
      <div ref={headerBottomRef} className="header-full__bottom">
        <div className="header-full__container">
          <div className="header-full__menu">
            <Poly
              className="header-full__logo"
              {...data.settings.logo.ctaAnchor}
            >
              {data.settings.logo.image}
            </Poly>
            {/* submenu with dropdown on hover */}
            {/* TODO  add aria-label in translations object */}
            <nav role="navigation" aria-label="seconday-desktop-navigation">
              <ul className="header-full__desktop-submenu">
                <SubmenuButtons
                  submenus={currentMenu.submenu}
                  currentSubmenu={currentSubmenu}
                  translations={data.settings.translations}
                />
              </ul>
            </nav>
          </div>
          <div className="header-full__actions">
            <div className="header-full__search">
              <button
                // TODO check if aria-label can come from trasnslations object
                aria-label={isSearchOpen ? "Close search" : "Open search"}
                type="button"
                onClick={manageSearchOpening}
              >
                {isSearchOpen ? (
                  <GeneralMediumClose />
                ) : (
                  <GeneralMediumSearch />
                )}
              </button>
            </div>
            <div className="header-full__hamburger-menu">
              <HamburgerMenu
                menuOpeningStatus={menuOpeningStatus}
                onClick={manageMenuOpeningForHamburgerMenu}
              />
            </div>
            <div className="header-full__buttons">
              <PrivateAreaButtons privateArea={data.settings.privateArea} />
            </div>
          </div>
        </div>
      </div>
      {/* Mobile submenu */}
      <div className="header-full__mobile-submenu">
        <MenuOverlaySlider
          isOpen={menuOpeningStatus === "submenuOpen"}
          isSlideRight={menuOpeningStatus === "groupOpen"}
          fullClose={menuOpeningStatus === "fullClose"}
        >
          {/* TODO add this aria-label in traslations object */}
          <nav role="navigation" aria-label="seconday-mobile-navigation">
            <ul>
              <SubmenuButtons
                submenus={currentMenu.submenu}
                currentSubmenu={currentSubmenu}
                translations={data.settings.translations}
                onClick={setMenuOpeningStatus}
              />
            </ul>
          </nav>
          <div className="header-full__buttons--mobile">
            <PrivateAreaButtons privateArea={data.settings.privateArea} />
          </div>
          <div className="header-full__mobile-lang-select">
            <LangSelect hasBackground />
          </div>
        </MenuOverlaySlider>
      </div>
      {/* Search overlay */}
      {searchContent && (
        <SearchOverlay isOpen={isSearchOpen}>{searchContent}</SearchOverlay>
      )}
    </header>
  );
};

export default HeaderFull;
